import React, { useState, useEffect } from "react";
import { Flex, FormLabel, Text, HStack } from "@chakra-ui/react";
import ChakraPopUp from "../common/PopUp/ChakraPopUp";
import { fetchWithToken } from "../../functions/fetch";
import { useQueryClient } from "@tanstack/react-query";
import uniqid from "uniqid";
import { useFormik } from "formik";
import FormikSelect from "../common/PopUp/ChakraFormik/FormikSelect";
import DateRange from "../common/PopUp/ChakraFormik/DateRange";
import ParkingSpaceInput from "../common/PopUp/ChakraFormik/ParkingSpaceInput";
import FormikInput from "../common/PopUp/ChakraFormik/FormikInput";
import { createLeaseSchema } from "../../validation/leaseValidation";
import { DateTime } from "luxon";
import { carFeeType, scooterFeeType } from "../../config/config";
import FormikTextarea from "../common/PopUp/ChakraFormik/FormikTextarea";
import { MultiSelect } from "react-multi-select-component";
import { useToast } from "@chakra-ui/react";

const NewLeasePopUp = ({ isOpen, onClose, defaultValues }) => {
  const [scooterParkingSpaces, setScooterParkingSpaces] = useState([]);
  const [carParkingSpaces, setCarParkingSpaces] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const toast = useToast();
  const [errorMessages, setErrorMessages] = useState([]);

  const multiSelectChineseStr = {
    allItemsAreSelected: "已選擇所有選項",
    clearSearch: "清除搜尋",
    clearSelected: "清除已選擇選項",
    noOptions: "無選項",
    search: "搜尋",
    selectAll: "選擇全部",
    selectAllFiltered: "選擇全部（已過濾）",
    selectSomeItems: "選擇...",
    create: "新增",
  };

  // fetching
  const queryClient = useQueryClient();

  const fetchRentableRooms = async (startDate, endDate) => {
    const res = await fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/room/rentable?startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status === 200) {
      const { rooms } = res;
      // sort by room number
      setRooms(
        rooms.sort((a, b) => {
          const roomNum1 = a.roomNumber.replace(/F/g, "4");
          const roomNum2 = b.roomNumber.replace(/F/g, "4");
          return roomNum1.localeCompare(roomNum2);
        }),
      );
    } else {
      console.error(res);
    }
  };

  const fetchRentableUsers = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      return [];
    }

    try {
      const res = await fetchWithToken(
        "GET",
        `${process.env.REACT_APP_BACKEND_HOST}/api/user/rentable?startDate=${startDate}&endDate=${endDate}`,
      );
      if (res.status === 200) {
        setUsers(res.users);
      } else {
        console.error(res);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // type: CAR|SCOOTER
  const fetchRentableParkingSpace = async (startDate, endDate, type) => {
    const res = await fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/parking-space/rentable?startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status === 200) {
      const { parkingSpaces } = res;
      parkingSpaces.sort((a, b) => a.number - b.number);
      if (type === "SCOOTER") {
        setScooterParkingSpaces(
          parkingSpaces.filter((ps) => ps.type === "機車"),
        );
      } else if (type === "CAR") {
        setCarParkingSpaces(parkingSpaces.filter((ps) => ps.type === "汽車")); // sort by room number
      } else {
        console.warn("Invalid type for fetching rentable parking space");
      }
    } else {
      console.error(res);
    }
  };

  const getErrors = (formik) => {
    const { values, touched, errors } = formik;
    const arr = [];
    for (const prop in values) {
      if (touched[prop] && errors[prop]) {
        arr.push(errors[prop]);
      }
    }
    return arr.map((item) => (
      <Text color="red.500" key={uniqid()}>
        {item}
      </Text>
    ));
  };

  const getRenewDefaultValues = () => {
    // is empty object
    if (Object.keys(defaultValues).length === 0) {
      return {};
    }
    const { renters, room, startDate, endDate } = defaultValues;

    // start from the next day of endDate,have the same days of the original lease
    const diff = DateTime.fromISO(endDate).diff(DateTime.fromISO(startDate), [
      "days",
    ]);
    const { days } = diff.toObject(); // how many days are between start and end day

    const newStartDate = DateTime.fromISO(endDate).plus({ days: 1 });
    const newEndDate = newStartDate.plus({ days });

    return {
      renters: renters.map((renter) => {
        // for matching the multi select
        return {
          label: `${renter.idNumber} - ${renter.name}`,
          value: renter._id,
        };
      }),
      rentStartDate: newStartDate.toFormat("yyyy-MM-dd"),
      rentEndDate: newEndDate.toFormat("yyyy-MM-dd"),
      roomId: room._id,
      rentCost: room.cost, // NOTE: require admin to fill in again
    };
  };

  const formik = useFormik({
    initialValues: {
      roomId: "",
      renters: [],
      rentStartDate: "",
      rentEndDate: "",
      // scooter 1
      haveScooterParkingSpace: false,
      scooterParkingSpaceId: "",
      scooterLeaseStartDate: "",
      scooterLeaseEndDate: "",
      scooterFee: "",
      // scooter 2
      haveScooterParkingSpace2: false,
      scooterParkingSpaceId2: "",
      scooterLeaseStartDate2: "",
      scooterLeaseEndDate2: "",
      scooterFee2: "",
      // car
      haveCarParkingSpace: false,
      carParkingSpaceId: "",
      carLeaseStartDate: "",
      carLeaseEndDate: "",
      note: "",
      rentCost: "",
      carFee: "",
      ...getRenewDefaultValues(), // if default value exists, use it to renew
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      const body = values;
      body.renters = values.renters.map((renter) => renter.value); // the multi select returns {label, value}

      const res = await fetchWithToken(
        "POST",
        `${process.env.REACT_APP_BACKEND_HOST}/api/lease`,
        body,
      );
      if (res.status === 200) {
        // refetch
        queryClient.invalidateQueries({ queryKey: ["leases"] });
        queryClient.invalidateQueries({ queryKey: ["rentableRooms"] });
        queryClient.invalidateQueries({ queryKey: ["rooms"] });
        if (values.haveScooterParkingSpace || values.haveCarParkingSpace) {
          queryClient.invalidateQueries({
            queryKey: ["rentableParkingSpaces"],
          });
        }
        resetForm(); // clear input data
        onClose();
      } else {
        // there are overlapped leases
        const { chineseErrorMessage } = res;
        if (chineseErrorMessage) {
          setErrorMessages([chineseErrorMessage]);
        }
        console.error(res);
      }
      setIsSubmitting(false);
    },
    validationSchema: createLeaseSchema,
    enableReinitialize: true,
  });

  // when dates are changed, clear room field, and refetch rooms
  useEffect(() => {
    const { rentStartDate, rentEndDate } = formik.values;
    // both values exist
    if (
      rentStartDate?.length > 0 &&
      rentEndDate?.length > 0 &&
      new Date(rentStartDate) <= new Date(rentEndDate)
    ) {
      // when first rendering renewed lease, don't clear the room, use original room
      const {
        rentStartDate: defaultStart,
        rentEndDate: defaultEnd,
        roomId: defaultRoom,
        renters: defaultRenters,
      } = getRenewDefaultValues();

      if (rentStartDate === defaultStart && rentEndDate === defaultEnd) {
        formik.setFieldValue("roomId", defaultRoom);
        formik.setFieldValue("renters", defaultRenters);
      } else {
        formik.setFieldValue("roomId", ""); // clear the room
        formik.setFieldValue("renters", []);
      }
      fetchRentableRooms(rentStartDate, rentEndDate); // refetch
      fetchRentableUsers(rentStartDate, rentEndDate);
    }
  }, [formik.values.rentStartDate, formik.values.rentEndDate]);

  // when dates for parkingSpaces are changed, clear parking space field, and refetch them
  // car parking space
  useEffect(() => {
    const { carLeaseStartDate, carLeaseEndDate, haveCarParkingSpace } =
      formik.values;
    // need refetch car parking space
    if (
      haveCarParkingSpace &&
      carLeaseStartDate?.length > 0 &&
      carLeaseEndDate?.length > 0 &&
      new Date(carLeaseStartDate) <= new Date(carLeaseEndDate)
    ) {
      formik.setFieldValue("carParkingSpaceId", ""); // clear the room
      fetchRentableParkingSpace(carLeaseStartDate, carLeaseEndDate, "CAR"); // refetch
    }
  }, [
    formik.values.carLeaseStartDate,
    formik.values.carLeaseEndDate,
    formik.values.haveCarParkingSpace,
  ]);

  // scooter parking space
  useEffect(() => {
    const {
      scooterLeaseStartDate,
      scooterLeaseEndDate,
      haveScooterParkingSpace,
    } = formik.values;
    // need refetch scooter parking space
    if (
      haveScooterParkingSpace &&
      scooterLeaseStartDate?.length > 0 &&
      scooterLeaseEndDate?.length > 0 &&
      new Date(scooterLeaseStartDate) <= new Date(scooterLeaseEndDate)
    ) {
      formik.setFieldValue("scooterParkingSpaceId", ""); // clear the room
      fetchRentableParkingSpace(
        scooterLeaseStartDate,
        scooterLeaseEndDate,
        "SCOOTER",
      ); // refetch
    }
  }, [
    formik.values.scooterLeaseStartDate,
    formik.values.scooterLeaseEndDate,
    formik.values.haveScooterParkingSpace,
  ]);

  // after date is changed, need to refetch scooter parking space
  useEffect(() => {
    const {
      scooterLeaseStartDate2,
      scooterLeaseEndDate2,
      haveScooterParkingSpace2,
    } = formik.values;

    if (
      haveScooterParkingSpace2 &&
      scooterLeaseStartDate2?.length > 0 &&
      scooterLeaseEndDate2?.length > 0 &&
      new Date(scooterLeaseStartDate2) <= new Date(scooterLeaseEndDate2)
    ) {
      formik.setFieldValue("scooterParkingSpaceId2", ""); // clear the room
      fetchRentableParkingSpace(
        scooterLeaseStartDate2,
        scooterLeaseEndDate2,
        "SCOOTER",
      ); // refetch
    }
  }, [
    formik.values.scooterLeaseStartDate2,
    formik.values.scooterLeaseEndDate2,
    formik.values.haveScooterParkingSpace2,
  ]);

  useEffect(() => {
    const { roomId } = formik.values;
    const currentRoom = rooms.find((room) => room._id === roomId);
    formik.setFieldValue("rentCost", currentRoom?.cost ?? "");
  }, [formik.values.roomId, rooms]);

  // check if renters exceed the limit
  useEffect(() => {
    const maxRenterCount = 2;
    const { length } = formik.values.renters;

    if (length <= maxRenterCount) return;

    // show a toast to warn that there can only be two renters
    toast({
      title: `最多只能選擇${maxRenterCount}位住戶`,
      status: "warning",
      isClosable: true,
    });
    formik.setFieldValue(
      "renters",
      formik.values.renters.slice(0, maxRenterCount),
    );
  }, [formik.values.renters]);

  // when checking the haveScooterParkingSpace, use rent date for the scooter rent date
  useEffect(() => {
    if (!formik.values.haveScooterParkingSpace) {
      return;
    }

    formik.setFieldValue("scooterLeaseStartDate", formik.values.rentStartDate);
    formik.setFieldValue("scooterLeaseEndDate", formik.values.rentEndDate);
  }, [formik.values.haveScooterParkingSpace]);

  // when checking the haveScooterParkingSpace2, use rent date for the scooter rent date2
  useEffect(() => {
    if (!formik.values.haveScooterParkingSpace2) {
      return;
    }

    formik.setFieldValue("scooterLeaseStartDate2", formik.values.rentStartDate);
    formik.setFieldValue("scooterLeaseEndDate2", formik.values.rentEndDate);
  }, [formik.values.haveScooterParkingSpace2]);

  // when checking the haveScooterParkingSpace, use rent date for the car rent date
  useEffect(() => {
    if (!formik.values.haveCarParkingSpace) {
      return;
    }

    formik.setFieldValue("carLeaseStartDate", formik.values.rentStartDate);
    formik.setFieldValue("carLeaseEndDate", formik.values.rentEndDate);
  }, [formik.values.haveCarParkingSpace]);

  return (
    <ChakraPopUp
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      size="4xl"
      title="新增租約"
    >
      <Flex direction="column" gap={2}>
        <DateRange
          formik={formik}
          field1="rentStartDate"
          field2="rentEndDate"
          isRequired
        />
        <HStack>
          <FormikSelect
            labelText="房號："
            formik={formik}
            field="roomId"
            isRequired
          >
            <option value="">無</option>
            {rooms.map((room) => (
              <option key={uniqid()} value={room._id}>
                {room.roomNumber}
              </option>
            ))}
          </FormikSelect>
          <FormikInput
            formik={formik}
            labelText="房租："
            field="rentCost"
            isRequired
          />
        </HStack>
        <Flex alignItems={"center"}>
          <FormLabel mb={0}>住戶身分證：</FormLabel>
          <MultiSelect
            options={users.map((user) => {
              return {
                label: `${user.idNumber} - ${user.name}`,
                value: user._id,
              };
            })}
            className="w-[60%]"
            value={formik.values.renters}
            onChange={(newValues) => {
              formik.setFieldValue("renters", newValues);
            }}
            hasSelectAll={false}
            overrideStrings={multiSelectChineseStr}
          />
        </Flex>

        <ParkingSpaceInput
          formik={formik}
          haveSpaceField="haveScooterParkingSpace"
          labelText="機車位編號"
          startDateField="scooterLeaseStartDate"
          idField="scooterParkingSpaceId"
          endDateField="scooterLeaseEndDate"
          feeLabelText={scooterFeeType}
          feeField="scooterFee"
          dateLabel="機車租期："
        >
          <option value="">無</option>
          {scooterParkingSpaces.map((ps) => (
            <option key={uniqid()} value={ps._id}>
              {ps.number}
            </option>
          ))}
        </ParkingSpaceInput>
        <ParkingSpaceInput
          formik={formik}
          haveSpaceField="haveScooterParkingSpace2"
          labelText="機車位編號"
          startDateField="scooterLeaseStartDate2"
          idField="scooterParkingSpaceId2"
          endDateField="scooterLeaseEndDate2"
          feeLabelText={scooterFeeType}
          feeField="scooterFee2"
          dateLabel="機車租期："
        >
          <option value="">無</option>
          {scooterParkingSpaces.map((ps) => (
            <option key={uniqid()} value={ps._id}>
              {ps.number}
            </option>
          ))}
        </ParkingSpaceInput>
        <ParkingSpaceInput
          labelText="汽車位編號"
          formik={formik}
          idField="carParkingSpaceId"
          startDateField="carLeaseStartDate"
          endDateField="carLeaseEndDate"
          haveSpaceField="haveCarParkingSpace"
          feeLabelText={carFeeType}
          feeField="carFee"
          dateLabel="汽車租期："
        >
          <option value="">無</option>

          {carParkingSpaces.map((ps) => (
            <option key={uniqid()} value={ps._id}>
              {ps.number}
            </option>
          ))}
        </ParkingSpaceInput>
        <FormikTextarea formik={formik} field="note" labelText="備註：" />
        {getErrors(formik)}
        {errorMessages.map((text) => (
          <Text color="red.500" whiteSpace="pre" key={uniqid()}>
            {text}
          </Text>
        ))}
      </Flex>
    </ChakraPopUp>
  );
};

export default NewLeasePopUp;
